import { useQuery, useZero } from "@rocicorp/zero/react";
import { useMemo } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import type { ScopeKind } from "../../../../shared/rbac/permissions";
import {
  BUILTIN_ROLES,
  type BuiltinRoleId,
} from "../../../../shared/rbac/roles";
import { useGroupMemberships } from "../../../hooks/useGroupMemberships";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useResolveScopeName } from "../../../hooks/useResolveScopeName";
import {
  buildRoleRows,
  filterVisibleScopes,
  findGroupsForRole,
  findServiceAccountsForRole,
  findUsersForRole,
} from "./lib";
import { InspectRoleModal } from "./InspectRoleModal";

export function InspectRoleModalById({
  roleId,
  onClose,
}: {
  roleId: string;
  onClose: () => void;
}) {
  const z = useZero();
  const canManageRoles = useHasAccess("tenant:roles_manage");
  const [customRoles, rolesResult] = useQuery(queries.allRolesLite());
  const [allUserGroups, groupsResult] = useQuery(queries.allUserGroups());
  const { memberCounts: groupMemberCounts } = useGroupMemberships();
  const [allUsers, usersResult] = useQuery(queries.allUsers());
  const [allDirectUserGrantsRaw, userGrantsResult] = useQuery(
    queries.allDirectUserGrants()
  );
  const [allDirectServiceAccountGrants, serviceAccountGrantsResult] = useQuery(
    queries.allDirectServiceAccountGrants()
  );
  const { resolveScopeName, personalSpaceIds } = useResolveScopeName();

  const assignmentsReady = [
    rolesResult,
    groupsResult,
    usersResult,
    userGrantsResult,
    serviceAccountGrantsResult,
  ].every((result) => result.type === "complete");

  const allDirectUserGrants = useMemo(
    () =>
      allDirectUserGrantsRaw.filter((g) => !personalSpaceIds.has(g.scopeId)),
    [allDirectUserGrantsRaw, personalSpaceIds]
  );

  const userMap = useMemo(
    () => new Map(allUsers.map((user) => [user.id, user])),
    [allUsers]
  );

  const allRoleRows = useMemo(
    () => buildRoleRows(customRoles, new Map()),
    [customRoles]
  );

  const row = allRoleRows.find((r) => r.id === roleId);
  const builtinFallback = !row
    ? BUILTIN_ROLES[roleId as BuiltinRoleId]
    : undefined;
  if (!row && !builtinFallback) return null;

  const role = row
    ? {
        id: row.id,
        name: row.name,
        scopeKind: row.scope,
        permissions: row.permissions,
        isBuiltin: row.isBuiltin,
      }
    : {
        id: roleId,
        name: builtinFallback!.label,
        scopeKind: builtinFallback!.scopeKind as ScopeKind,
        permissions: builtinFallback!.permissions,
        isBuiltin: true,
      };

  const assignedGroups =
    role.scopeKind !== "user_group"
      ? filterVisibleScopes(
          findGroupsForRole(
            allUserGroups,
            role.id,
            role.scopeKind,
            role.isBuiltin
          ),
          role.scopeKind,
          resolveScopeName
        )
      : undefined;

  const assignedUsers = filterVisibleScopes(
    findUsersForRole(allDirectUserGrants, role.id, role.isBuiltin, userMap),
    role.scopeKind,
    resolveScopeName
  );

  const assignedServiceAccounts = filterVisibleScopes(
    findServiceAccountsForRole(
      allDirectServiceAccountGrants,
      role.id,
      role.isBuiltin
    ),
    role.scopeKind,
    resolveScopeName
  );

  return (
    <InspectRoleModal
      role={role}
      onClose={onClose}
      onDelete={(roleId) => {
        z.mutate(sharedMutators.deleteRole({ roleId }));
        onClose();
      }}
      canManage={canManageRoles}
      assignmentsReady={assignmentsReady}
      assignedGroups={assignedGroups}
      assignedUsers={assignedUsers}
      assignedServiceAccounts={assignedServiceAccounts}
      groupMemberCounts={groupMemberCounts}
      resolveScopeName={resolveScopeName}
    />
  );
}
